<template>
  <CollapsableSection
    :collapse="shouldCollapseSection"
    title="RADIUS Reply Attributes"
    :unsetOverflow="true"
  >
    <KnowledgeBaseArticle
      :class="$style.kbArticle"
      :includeInfoIcon="false"
      :infoText="learnMore.infoText"
      :kbLink="learnMore.kbLink"
      :kbText="learnMore.kbText"
    />
    <ListEditor
      v-if="attributes.length"
      buttonText=""
      component="CustomAttributeFields"
      :dynamicErrorList="dynamicErrorList"
      :handleChangeListItem="handleChangeListItem"
      :handleRemoveListItem="handleRemoveAttribute"
      headerText="Attribute Mapping"
      :listItems="replyAttributes"
      nameTitle="RADIUS Attribute Name"
      nameValidation=""
      valueMaxLengthValidation="253"
      valueTitle="RADIUS Attribute Value"
      valueValidation="maxCharacterValidation"
    />
    <div
      :class="$style.buttonsContainer"
    >
      <FlatActionButton
        :action="handleAddAttribute"
        :class="['btnDefault', $style.addAttributeBtn]"
        :disabled="shouldDisableAddButton"
        text="add new attribute"
      />
    </div>
  </CollapsableSection>
</template>

<script>
import { ItemTypeConsts, StoreModelMap, StoreModuleMap } from 'client/util/Constants';
import { capitalize, cloneDeep, trim } from 'lodash';
import { mapActions, mapGetters } from 'vuex';
import AsidePanelChildComponentMethodsMixin from 'client/components/AsidePanel/mixins/AsidePanelChildComponentMethodsMixin';
import AsidePanelChildComponentPropsMixin from 'client/components/AsidePanel/mixins/AsidePanelChildComponentPropsMixin';
import CollapsableSection from 'client/components/ContentSection/CollapsableSection.vue';
import FlatActionButton from 'client/components/Buttons/FlatActionButton.vue';
import GroupsConstants from 'client/util/Constants/GroupsConstants';
import KbArticleUrls from 'client/util/Constants/KbArticleUrls';
import KnowledgeBaseArticle from 'client/components/FormValidation/KnowledgeBaseArticle.vue';
import ListEditor from 'client/components/FormValidation/ListEditor.vue';

const { dynamiceErrorUpdateMode } = GroupsConstants;
let userGroupModel = StoreModelMap[ItemTypeConsts.userGroup];
const asidePanelEventsModule = StoreModuleMap[ItemTypeConsts.asidePanelEvents];

export default {
  name: 'RadiusReplyAttributes',

  components: {
    CollapsableSection,
    FlatActionButton,
    KnowledgeBaseArticle,
    ListEditor,
  },

  inject: ['$validator'],

  mixins: [
    AsidePanelChildComponentMethodsMixin,
    AsidePanelChildComponentPropsMixin,
  ],

  data: () => ({
    addButtonClassName: 'addButtonClassName',
    learnMore: {
      infoText: 'Create and store RADIUS Reply Attributes for users in this user group, such as attributes for VLAN tagging. These attributes will be passed to RADIUS servers associated with this user group. Refer to your hardware configuration documentation to find which attributes to add.',
      kbText: 'Learn more about VLAN tagging and how to use Reply Attributes.',
      kbLink: KbArticleUrls.userGroupsRadiusReplyAttributes,
    },
  }),

  computed: {
    ...mapGetters(userGroupModel, {
      attributes: 'getRadiusReplyAttributes',
      dynamicErrorList: 'getRadiusReplyErrors',
    }),

    ...mapGetters(asidePanelEventsModule, {
      attributesErrorDetails: 'getAsidePanelError',
    }),

    replyAttributes() {
      return this.attributes || [];
    },

    shouldCollapseSection() {
      return !this.replyAttributes;
    },

    shouldDisableAddButton() {
      return this.replyAttributes.length >= 50;
    },
  },

  watch: {
    attributesErrorDetails(errorDetails) {
      if (errorDetails.length > 0) {
        let errorIndex;
        let errorText;
        const radiusReplyErrors = errorDetails[0].fieldViolationsList.map((violation) => {
          errorIndex = this.extractIndexFromViolationField(violation.field);
          errorText = this.extractErrorTextFromFullDescription(violation.description);

          return { errorIndex: parseInt(errorIndex, 10), errorText, shouldShow: true };
        });

        this.updateRadiusReplyErrors({ radiusReplyErrors });
      }
    },
  },

  beforeCreate() {
    this.$options.methods.asidePanelChildComponentHandleBeforeCreate(this.$options);
    userGroupModel = StoreModelMap[this.$options.propsData.parentType];
  },

  beforeMount() {
    const maxCharacterValue = value => value.length < 253;

    this.$validator.extend('maxCharacterValidation', {
      getMessage: '* 253 character limit.',
      validate: maxCharacterValue,
    });
  },

  methods: {
    ...mapActions(userGroupModel, [
      'addNewRadiusReplyAttributeRow',
      'removeRadiusReplyAttribute',
      'updateRadiusReplyAttribute',
      'updateRadiusReplyErrors',
    ]),

    beautifyErrorMessage(violationDescription, stringSlice = '') {
      // This string handling will cut out anything before the given `stringSlice` param
      // Essentially, to shorten the error message for better UI exposure when rendering
      const regex = new RegExp(`(.*)${stringSlice}`);
      const beautyErrorMessage = trim(violationDescription.replace(regex, ''));

      return stringSlice !== '' ? `* ${capitalize(beautyErrorMessage)}` : `* ${capitalize(violationDescription)}`;
    },

    extractErrorTextFromFullDescription(violationDescription) {
      const hasTagError = violationDescription.indexOf(':') > -1;
      const hasTagRangeError = violationDescription.indexOf(';') > -1;

      if (hasTagRangeError) {
        return this.beautifyErrorMessage(violationDescription, ';');
      }

      if (hasTagError) {
        return this.beautifyErrorMessage(violationDescription, ':');
      }

      return this.beautifyErrorMessage(violationDescription);
    },

    extractIndexFromViolationField(violationField) {
      return violationField.replace(/[^0-9]/g, '');
    },

    handleAddAttribute() {
      this.addNewRadiusReplyAttributeRow();
    },

    handleChangeListItem(index, listItem, columName) {
      if (this.attributesErrorDetails.length > 0 && columName === 'name') {
        this.handleUpdateDynamicErrorList(index, dynamiceErrorUpdateMode.update);
      }

      this.updateRadiusReplyAttribute({ index, listItem });
    },

    handleRemoveAttribute(index) {
      if (this.attributesErrorDetails.length > 0) {
        this.handleUpdateDynamicErrorList(index, dynamiceErrorUpdateMode.remove);
      }
      this.removeRadiusReplyAttribute({ index });
    },

    handleUpdateDynamicErrorList(index, updateMode) {
      const updatedRadiusReplyErrors = cloneDeep(this.dynamicErrorList);
      const errorIndexToUpdate = updatedRadiusReplyErrors.findIndex(
        error => (error.errorIndex === index),
      );

      if (errorIndexToUpdate >= 0 && updateMode === dynamiceErrorUpdateMode.update) {
        updatedRadiusReplyErrors[errorIndexToUpdate].shouldShow = false;
        this.updateRadiusReplyErrors({ radiusReplyErrors: updatedRadiusReplyErrors });
      }

      if (errorIndexToUpdate >= 0 && updateMode === dynamiceErrorUpdateMode.remove) {
        updatedRadiusReplyErrors.splice(errorIndexToUpdate, 1);

        updatedRadiusReplyErrors.forEach((error, internalIndex) => {
          if (error.errorIndex > index) {
            updatedRadiusReplyErrors[internalIndex].errorIndex -= 1;
          }
        });
        this.updateRadiusReplyErrors({ radiusReplyErrors: updatedRadiusReplyErrors });
      }
    },
  },
};
</script>

<style module>
@import '../../client/components/FormValidation/css/formFields.css';

@value (white) from '~css/colors.css';

.addAttributeBtn {
  background-color: jcSuccess;
}

.buttonsContainer {
  align-items: baseline;
  display: flex;
  flex-direction: row;
}

.buttonsContainer > :not(:last-child) {
  margin-right: 10px;
}

.kbArticle {
  margin-bottom: 18px !important;
}
</style>
